import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="admin-booking"
// Handles dynamic UI updates for manual class booking form
export default class extends Controller {
  static targets = [
    "memberSelect",
    "sessionSelect",
    "sessionName",
    "sessionDate",
    "sessionTime",
    "sessionInstructor",
    "sessionLocation",
    "sessionCapacity",
    "sessionCredits",
    "sessionAvailability",
    "sessionInfoPanel",
    "seatSelectionPanel",
    "seatSelect",
    "bookingTypeSelect",
    "submitButton"
  ]

  connect() {
    // Store selected values (combobox values are in hidden fields, not directly accessible)
    this.selectedMemberId = null
    this.selectedSessionId = null

    // Initial update if values are pre-selected (check hidden inputs)
    setTimeout(() => {
      const memberInput = document.querySelector('input[name="member_id"]')
      const sessionInput = document.querySelector('input[name="organization_session_id"]')

      if (memberInput && memberInput.value) {
        this.selectedMemberId = memberInput.value
      }
      if (sessionInput && sessionInput.value) {
        this.selectedSessionId = sessionInput.value
        this.fetchSessionInfo(this.selectedSessionId)
      }
      this.updateSubmitButton()
    }, 100)
  }

  // Called when session selection changes (receives hw-combobox:selection event)
  updateSessionInfo(event) {
    // Get value from event detail (hotwire_combobox passes value in event.detail)
    const sessionId = event?.detail?.value || ''
    this.selectedSessionId = sessionId || null

    if (!sessionId) {
      this.resetSessionInfo()
      this.updateSubmitButton()
      return
    }

    this.fetchSessionInfo(sessionId)
  }

  // Fetch session info from server
  fetchSessionInfo(sessionId) {
    fetch(`/bookings/session_info?session_id=${sessionId}`)
      .then(response => {
        if (!response.ok) throw new Error('Session not found')
        return response.json()
      })
      .then(data => {
        this.displaySessionInfo(data)
        this.checkSeatSelection(sessionId, data)
        this.updateSubmitButton(data)
      })
      .catch(error => {
        console.error('Error fetching session info:', error)
        this.resetSessionInfo()
      })
  }

  displaySessionInfo(data) {
    // Show the info panel
    if (this.hasSessionInfoPanelTarget) {
      this.sessionInfoPanelTarget.style.display = 'block'
      this.sessionInfoPanelTarget.classList.remove('hidden')
    }

    // Update individual text elements safely using textContent
    if (this.hasSessionNameTarget) {
      this.sessionNameTarget.textContent = data.name
    }
    if (this.hasSessionDateTarget) {
      this.sessionDateTarget.textContent = data.date
    }
    if (this.hasSessionTimeTarget) {
      this.sessionTimeTarget.textContent = `${data.start_time} - ${data.end_time}`
    }
    if (this.hasSessionInstructorTarget) {
      this.sessionInstructorTarget.textContent = data.instructor_name
    }
    if (this.hasSessionLocationTarget) {
      this.sessionLocationTarget.textContent = data.location_name
    }
    if (this.hasSessionCapacityTarget) {
      this.sessionCapacityTarget.textContent = `${data.current_bookings}/${data.max_capacity}`
    }
    if (this.hasSessionCreditsTarget) {
      this.sessionCreditsTarget.textContent = `${data.credits_required} credit${data.credits_required !== 1 ? 's' : ''}`
    }
    if (this.hasSessionAvailabilityTarget) {
      const target = this.sessionAvailabilityTarget
      // Clear existing classes
      target.classList.remove('text-green-600', 'text-orange-600', 'text-red-600')

      if (data.spots_available) {
        target.textContent = `${data.spots_remaining} spots available`
        target.classList.add('text-green-600')
      } else if (data.waitlist_enabled) {
        target.textContent = `Full - Waitlist available (${data.waitlist_count} waiting)`
        target.classList.add('text-orange-600')
      } else {
        target.textContent = 'Full - No waitlist'
        target.classList.add('text-red-600')
      }
    }
  }

  checkSeatSelection(sessionId, data) {
    if (!this.hasSeatSelectionPanelTarget) return

    if (data.seat_selection_enabled) {
      this.seatSelectionPanelTarget.style.display = 'block'
      this.loadAvailableSeats(sessionId)
    } else {
      this.seatSelectionPanelTarget.style.display = 'none'
      if (this.hasSeatSelectTarget) {
        // Clear and add default option
        this.seatSelectTarget.length = 0
        const opt = document.createElement('option')
        opt.value = ''
        opt.textContent = '-- No seat selection for this class --'
        this.seatSelectTarget.appendChild(opt)
      }
    }
  }

  loadAvailableSeats(sessionId) {
    fetch(`/bookings/available_seats?session_id=${sessionId}`)
      .then(response => response.json())
      .then(data => {
        if (data.seats && data.seats.length > 0) {
          this.populateSeatDropdown(data.seats)
        } else {
          if (this.hasSeatSelectTarget) {
            this.seatSelectTarget.length = 0
            const opt = document.createElement('option')
            opt.value = ''
            opt.textContent = '-- No seats available --'
            this.seatSelectTarget.appendChild(opt)
          }
        }
      })
      .catch(error => {
        console.error('Error loading seats:', error)
      })
  }

  populateSeatDropdown(seats) {
    if (!this.hasSeatSelectTarget) return

    // Clear existing options
    this.seatSelectTarget.length = 0

    // Add default option
    const defaultOpt = document.createElement('option')
    defaultOpt.value = ''
    defaultOpt.textContent = '-- Select Seat (Optional) --'
    this.seatSelectTarget.appendChild(defaultOpt)

    // Add seat options
    seats.forEach(seat => {
      const option = document.createElement('option')
      option.value = seat.id
      option.textContent = seat.row ? `Row ${seat.row} - Seat ${seat.seat_number}` : `Seat ${seat.seat_number}`
      this.seatSelectTarget.appendChild(option)
    })
  }

  updateSubmitButton(sessionData = null) {
    if (!this.hasSubmitButtonTarget) return

    // Use stored values (populated from combobox events)
    const memberSelected = !!this.selectedMemberId
    const sessionSelected = !!this.selectedSessionId

    // Enable/disable button based on required selections
    this.submitButtonTarget.disabled = !(memberSelected && sessionSelected)

    // Update button text + style based on availability (canonical owner
    // button classes; the view renders owner-btn-primary by default)
    if (sessionData && !sessionData.spots_available && sessionData.waitlist_enabled) {
      this.submitButtonTarget.textContent = 'Add to Waitlist'
      this.submitButtonTarget.classList.remove('owner-btn-primary')
      this.submitButtonTarget.classList.add('owner-btn-secondary')
    } else {
      this.submitButtonTarget.textContent = 'Create Booking'
      this.submitButtonTarget.classList.add('owner-btn-primary')
      this.submitButtonTarget.classList.remove('owner-btn-secondary')
    }
  }

  // Called when member selection changes (receives hw-combobox:selection event)
  updateMemberInfo(event) {
    // Get value from event detail (hotwire_combobox passes value in event.detail)
    this.selectedMemberId = event?.detail?.value || null
    this.updateSubmitButton()
  }

  resetSessionInfo() {
    // Hide session info panel
    if (this.hasSessionInfoPanelTarget) {
      this.sessionInfoPanelTarget.style.display = 'none'
    }

    // Hide seat selection
    if (this.hasSeatSelectionPanelTarget) {
      this.seatSelectionPanelTarget.style.display = 'none'
    }

    // Clear text fields
    if (this.hasSessionNameTarget) this.sessionNameTarget.textContent = ''
    if (this.hasSessionDateTarget) this.sessionDateTarget.textContent = ''
    if (this.hasSessionTimeTarget) this.sessionTimeTarget.textContent = ''
    if (this.hasSessionInstructorTarget) this.sessionInstructorTarget.textContent = ''
    if (this.hasSessionLocationTarget) this.sessionLocationTarget.textContent = ''
    if (this.hasSessionCapacityTarget) this.sessionCapacityTarget.textContent = ''
    if (this.hasSessionCreditsTarget) this.sessionCreditsTarget.textContent = ''
    if (this.hasSessionAvailabilityTarget) this.sessionAvailabilityTarget.textContent = ''
  }
}
